import { Plus, X } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ChatRoomListSection } from "@/features/chat/components/ChatRoomListSection";
import { RecipientSelectorDialog } from "@/features/chat/components/RecipientSelectorDialog";
import { useChatSearchExecution } from "@/features/chat/hooks/useChatSearchExecution";
import { useParticipantsSearchState } from "@/features/chat/hooks/useParticipantsSearchState";
import { useChatSearchViewState } from "@/features/chat/hooks/useChatSearchViewState";
import { mapChatRoomsToListItems } from "@/features/chat/utils/chatRoomList";
import { buildKeywordSearchConfigs } from "@/features/chat/utils/keywordSearchConfigs";
import { useSearchChatEntries } from "@/hooks/useSearchChatEntries";
import { useSearchChatRooms } from "@/hooks/useSearchChatRooms";
import type { User, UserShort } from "@/lib/humhub-api/types";
import { useGlobalUserInfo } from "@/providers/AuthProvider";

const LAST_MESSAGE_MAX_LENGTH = 30;

const LoadingState: React.FC<{ label: string }> = ({ label }) => (
  <div className="py-8 text-center">
    <div className="mx-auto size-8 animate-spin rounded-full border-b-2 border-rw-main" />
    <p className="mt-2 text-sm text-gray-500">{label}</p>
  </div>
);

const buildHitReasonBadges = (
  t: (key: string) => string,
  hitReasons?: string[],
): ReactNode => {
  if (!hitReasons || hitReasons.length === 0) {
    return null;
  }

  const normalized = Array.from(new Set(hitReasons));
  const labels = normalized
    .map((reason) => {
      if (reason === "title") return t("chat.searchHitTitle");
      if (reason === "participant") return t("chat.searchHitParticipant");
      if (reason === "content") return t("chat.searchHitContent");
      return null;
    })
    .filter((label): label is string => Boolean(label));

  if (labels.length === 0) {
    return null;
  }

  return labels.map((label) => (
    <Badge
      key={label}
      variant="secondary"
      className="pointer-events-none select-none border border-rw-gray-200 bg-white px-1.5 py-0 text-[9px] leading-4 text-rw-mid-gray"
    >
      {label}
    </Badge>
  ));
};

const buildEntryMatchedRoomIds = (
  results?: Array<{ message_id?: number | null }>,
) =>
  new Set(
    (results ?? [])
      .map((entry) => entry.message_id)
      .filter((id): id is number => typeof id === "number"),
  );

interface ChatSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChatRoomSelect: (roomId: number | undefined) => void;
  onCreateNewChat?: (selectedUser?: User) => void;
  initialUser?: User;
  initialParticipants?: UserShort[];
  initialSearchMode?: "keyword" | "participants";
}

export const ChatSearchModal = ({
  isOpen,
  onClose,
  onChatRoomSelect,
  onCreateNewChat,
  initialUser,
  initialParticipants,
  initialSearchMode,
}: ChatSearchModalProps) => {
  const { t } = useTranslation();
  const searchModeIdBase = useId();
  const keywordSearchModeId = `${searchModeIdBase}-keyword`;
  const participantsSearchModeId = `${searchModeIdBase}-participants`;
  const [keyword, setKeyword] = useState("");
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(
    initialUser,
  );
  const [searchMode, setSearchMode] = useState<"keyword" | "participants">(
    "keyword",
  );
  const [selectedParticipants, setSelectedParticipants] = useState<UserShort[]>(
    [],
  );
  const [isParticipantDialogOpen, setIsParticipantDialogOpen] = useState(false);
  const userInfo = useGlobalUserInfo();

  // チャットルーム検索用のフック（キーワード検索：DM）
  const dmSearch = useSearchChatRooms(undefined, "user_dm");

  // チャットルーム検索用のフック（キーワード検索：トピック）
  const topicSearch = useSearchChatRooms(undefined, "user_group");
  const dmEntrySearch = useSearchChatEntries(undefined, "user_dm");
  const topicEntrySearch = useSearchChatEntries(undefined, "user_group");

  const dmEntryMatchedRoomIds = useMemo(
    () => buildEntryMatchedRoomIds(dmEntrySearch.response?.results),
    [dmEntrySearch.response?.results],
  );

  const topicEntryMatchedRoomIds = useMemo(
    () => buildEntryMatchedRoomIds(topicEntrySearch.response?.results),
    [topicEntrySearch.response?.results],
  );

  const {
    hasParticipants,
    existingDmChats,
    existingTopicChats,
    isCheckingExisting,
    error: participantsError,
  } = useParticipantsSearchState({
    isOpen,
    searchMode,
    selectedParticipants,
    currentUserId: userInfo?.id,
  });

  const selectedParticipantsSignature = useMemo(
    () =>
      selectedParticipants
        .map((participant) => participant.id)
        .sort((a, b) => a - b)
        .join(","),
    [selectedParticipants],
  );

  // initialUserが変更されたらselectedUserを更新
  useEffect(() => {
    setSelectedUser(initialUser);
    if (initialUser) {
      const userName =
        `${initialUser.profile.firstname} ${initialUser.profile.lastname}`.trim();
      setKeyword(userName);
    }
  }, [initialUser]);

  // キーワードが変更されたら検索をリセット
  // biome-ignore lint/correctness/useExhaustiveDependencies: 必要な場合のみ再実行
  useEffect(() => {
    if (isOpen) {
      setSearchPerformed(false);
    }
  }, [keyword, isOpen, searchMode, selectedParticipantsSignature]);

  const keywordSearchConfigs = useMemo(
    () =>
      buildKeywordSearchConfigs({
        t,
        dmSearch,
        topicSearch,
        dmEntryMatchedRoomIds,
        topicEntryMatchedRoomIds,
        lastMessageMaxLength: LAST_MESSAGE_MAX_LENGTH,
        buildHitReasonBadges: (hitReasons) =>
          buildHitReasonBadges(t, hitReasons),
      }),
    [t, dmSearch, dmEntryMatchedRoomIds, topicSearch, topicEntryMatchedRoomIds],
  );

  const { isSearching, handleSearch, handleKeyDown } = useChatSearchExecution({
    keyword,
    searchMode,
    hasParticipants,
    isCheckingExisting,
    keywordSearchConfigs,
    dmEntrySearch,
    topicEntrySearch,
    onSearchPerformed: () => setSearchPerformed(true),
  });

  // モーダルの開閉状態管理
  useEffect(() => {
    if (isOpen) {
      if (!initialUser) {
        setKeyword("");
        setSelectedUser(undefined);
      }
      setSelectedParticipants(initialParticipants ?? []);
      setSearchMode(initialSearchMode ?? "keyword");
      setSearchPerformed(false);
      isSearching.current = false;
    } else {
      isSearching.current = false;
      setKeyword("");
      setSelectedUser(undefined);
      setSelectedParticipants([]);
      setSearchMode("keyword");
      setSearchPerformed(false);
    }
  }, [
    isOpen,
    initialUser,
    initialParticipants,
    initialSearchMode,
    isSearching,
  ]);

  useEffect(() => {
    if (
      !isOpen ||
      searchMode !== "participants" ||
      !hasParticipants ||
      isCheckingExisting ||
      searchPerformed
    ) {
      return;
    }

    setSearchPerformed(true);
  }, [
    isOpen,
    searchMode,
    hasParticipants,
    isCheckingExisting,
    searchPerformed,
  ]);

  // 新規チャット作成ハンドラ
  const handleCreateNewChat = () => {
    if (onCreateNewChat) {
      onCreateNewChat(selectedUser);
    }
    onClose();
  };

  const handleChatRoomClick = (roomId: number | undefined) => {
    onChatRoomSelect(roomId);
    onClose();
  };

  const { isPreparingData, isLoading, hasError, errorMessage, noResults } =
    useChatSearchViewState({
      searchMode,
      searchPerformed,
      isCheckingExisting,
      participantsError,
      existingDmChats,
      existingTopicChats,
      keywordSearchConfigs,
    });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("chat.searchTitle")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-3">
            <RadioGroup
              value={searchMode}
              onValueChange={(value) =>
                setSearchMode(value as typeof searchMode)
              }
            >
              <label
                className="flex items-center gap-2 text-sm"
                htmlFor={keywordSearchModeId}
              >
                <RadioGroupItem value="keyword" id={keywordSearchModeId} />
                {t("chat.searchByKeyword")}
              </label>
              <label
                className="flex items-center gap-2 text-sm"
                htmlFor={participantsSearchModeId}
              >
                <RadioGroupItem
                  value="participants"
                  id={participantsSearchModeId}
                />
                {t("chat.searchByParticipants")}
              </label>
            </RadioGroup>

            {searchMode === "keyword" && (
              <div className="flex items-center space-x-2">
                <Input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t("chat.searchPlaceholder")}
                  className="flex-1"
                  disabled={isPreparingData}
                />
                <Button
                  onClick={handleSearch}
                  disabled={
                    !keyword.trim() ||
                    isLoading ||
                    isSearching.current ||
                    isPreparingData
                  }
                  variant="secondary"
                >
                  {t("common.search")}
                </Button>
              </div>
            )}

            {searchMode === "participants" && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setIsParticipantDialogOpen(true)}
                    disabled={isPreparingData}
                  >
                    <Plus size={14} className="mr-1" />
                    {t("chat.addSearchParticipant")}
                  </Button>
                  <Button
                    onClick={handleSearch}
                    disabled={
                      selectedParticipants.length === 0 ||
                      isLoading ||
                      isSearching.current ||
                      isPreparingData
                    }
                    variant="secondary"
                  >
                    {t("common.search")}
                  </Button>
                </div>

                {selectedParticipants.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedParticipants.map((participant) => (
                      <Badge
                        key={participant.guid}
                        variant="secondary"
                        className="flex items-center gap-1 bg-white px-2 py-1"
                      >
                        <span className="text-xs">
                          {participant.display_name}
                        </span>
                        <X
                          size={12}
                          className="cursor-pointer hover:text-destructive"
                          onClick={() =>
                            setSelectedParticipants((prev) =>
                              prev.filter((user) => user.id !== participant.id),
                            )
                          }
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {isPreparingData && <LoadingState label={t("common.loading")} />}

          {isLoading && !isPreparingData && (
            <LoadingState label={t("common.searching")} />
          )}

          {hasError && (
            <div className="py-4 text-center text-red-500">{errorMessage}</div>
          )}

          {noResults && (
            <div className="py-4 text-center text-gray-500">
              {t("chat.noSearchResults")}
            </div>
          )}

          {searchPerformed && !isLoading && !hasError && !isPreparingData && (
            <div className="max-h-[400px] space-y-4 overflow-y-auto">
              {searchMode === "participants" && existingDmChats.length > 0 && (
                <ChatRoomListSection
                  title={t("chat.sectionMessages")}
                  rooms={mapChatRoomsToListItems(existingDmChats, {
                    maxLastEntryLength: LAST_MESSAGE_MAX_LENGTH,
                  })}
                  onRoomClick={handleChatRoomClick}
                />
              )}

              {searchMode === "participants" &&
                existingTopicChats.length > 0 && (
                  <ChatRoomListSection
                    title={t("chat.sectionTopics")}
                    rooms={mapChatRoomsToListItems(existingTopicChats, {
                      useTitle: true,
                      maxLastEntryLength: LAST_MESSAGE_MAX_LENGTH,
                    })}
                    onRoomClick={handleChatRoomClick}
                  />
                )}

              {searchMode === "keyword" &&
                keywordSearchConfigs.map((config) => {
                  const rooms = config.response?.results || [];
                  if (rooms.length === 0) {
                    return null;
                  }
                  return (
                    <ChatRoomListSection
                      key={config.key}
                      title={config.title}
                      rooms={config.mapRooms(rooms)}
                      onRoomClick={handleChatRoomClick}
                    />
                  );
                })}
            </div>
          )}
        </div>

        <DialogFooter>
          {/* 新規チャット作成ボタン */}
          {onCreateNewChat && (
            <Button onClick={handleCreateNewChat} variant="teal">
              <Plus size={16} className="mr-2" />
              {t("chat.createNewChatRoom")}
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            {t("common.close")}
          </Button>
        </DialogFooter>
      </DialogContent>

      <RecipientSelectorDialog
        open={isParticipantDialogOpen}
        onClose={() => setIsParticipantDialogOpen(false)}
        onRecipientsSelected={(recipients) => {
          setSelectedParticipants(recipients);
          setIsParticipantDialogOpen(false);
        }}
        initialRecipients={selectedParticipants}
      />
    </Dialog>
  );
};
